import React from 'react';
import { IProcessManager } from '../../Interfaces/IProcessManager';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ILegalTransactionRelationship } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { ProcessManagerLandRegisterTable } from '../../ProcessManager/LandRegister/ProcessManagerLandRegisterTable';
import { ProcessManagerCommercialRegisterTable } from '../../ProcessManager/CommercialRegister/ProcessManagerCommercialRegisterTable';
import { ILegalPhaseFullObject } from '../../Interfaces/ILegalPhase';
import { ILegalRequirementState } from '../../Interfaces/ILegalRequirementState';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import { ILegalRole } from '../../Interfaces/ILegalRole';
import { useGetHbciAccounts } from '../../hooks/reactQuerys/HbciAccountQueries';
import { ProcessManagerHbciRow } from './ProcessManagerHbciRow';

interface IProps {
  processManagerArray: IProcessManager[];
  setProcessManagerArray: Function;
  //
  relationsships: ILegalTransactionRelationship[];
  setRelationships: Function;
  //
  legalClients: ILegalClient[];
  legalPhases: ILegalPhaseFullObject[];
  legalReqStates: ILegalRequirementState[];
  roles?: ILegalRole[];
}

/**
 * Section-Definition fuer den dynamisch sortierten Block.
 * ``firstId`` ist die kleinste positive idProcessManager in dieser Section
 * (= aelteste Eintragung im Vorgang). Sections werden danach sortiert,
 * damit „a)" immer die zuerst angelegte Kategorie ist — analog zur
 * Reihenfolge bei den Vertragsgegenstaenden.
 */
interface Section {
  key: string;
  firstId: number;
  render: (letter: string) => React.ReactNode;
}

export const ProcessManagerInformationTecking: React.FC<IProps> = ({
  processManagerArray,
  setProcessManagerArray,
  relationsships,
  setRelationships,
  legalClients,
  legalPhases,
  legalReqStates,
  roles = [],
}) => {
  // Pro Kategorie die anzuzeigenden PMs sammeln. Offene (State 1) UND erledigte
  // (State 3) bleiben sichtbar — damit eine gerade automatisch geschlossene
  // Nachverfolgung beim Öffnen des Vorgangs nicht komplett verschwindet, sondern
  // klar als "erledigt" erkennbar bleibt. Nur echte Nicht-Zustände (0=neu/
  // verworfen) belegen keinen Buchstaben; gelöschte (Deleted_at) liefert das
  // Backend ohnehin nicht mit. Negative IDs = noch nicht gespeichert.
  const isShown = (pm: IProcessManager) =>
    pm.idProcessManagerState === 1 ||
    pm.idProcessManagerState === 3 ||
    pm.idProcessManager < 0;

  const pmsLandRegister = processManagerArray.filter(
    pm => [10, 11].includes(pm.idProcessManagerType) && isShown(pm)
  );
  const pmsHandelsregister = processManagerArray.filter(
    pm => pm.idProcessManagerType === 20 && isShown(pm)
  );
  const pmsGesellschafterliste = processManagerArray.filter(
    pm => pm.idProcessManagerType === 21 && isShown(pm)
  );
  const pmsHbci = processManagerArray.filter(
    pm => pm.idProcessManagerType === 22 && isShown(pm)
  );

  /** Kleinste positive ID in der Kategorie = aelteste Eintragung. Wenn nur
   *  negative IDs (= noch nicht gespeichert) vorhanden sind, nehmen wir die
   *  kleinste negative — dann landet diese Kategorie nach allen
   *  persistierten, was sich intuitiv anfuehlt. */
  const oldestId = (arr: IProcessManager[]): number => {
    if (arr.length === 0) return Number.POSITIVE_INFINITY;
    const positives = arr.filter(p => p.idProcessManager > 0);
    if (positives.length > 0) {
      return Math.min(...positives.map(p => p.idProcessManager));
    }
    // Nur negative IDs (alle „Noch nicht gespeichert") — wir sortieren sie
    // ans Ende, aber stabil untereinander nach Insertion-Reihenfolge.
    return (
      Number.MAX_SAFE_INTEGER -
      Math.abs(Math.max(...arr.map(p => p.idProcessManager)))
    );
  };

  // idLegalTransaction aus erstem PM mit positiver Vorgang-ID — alle
  // gehoeren zum gleichen Vorgang.
  const idLegalTransaction =
    processManagerArray.find(
      pm => pm.idLegalTransaction !== null && (pm.idLegalTransaction ?? 0) > 0
    )?.idLegalTransaction ?? null;

  const { hbciAccounts } = useGetHbciAccounts(
    pmsHbci.length > 0 ? idLegalTransaction : null
  );

  const sections: Section[] = [];

  if (pmsLandRegister.length > 0) {
    sections.push({
      key: 'landregister',
      firstId: oldestId(pmsLandRegister),
      render: letter => (
        <Box key='landregister' sx={{ mb: 2 }}>
          <Typography sx={{ mt: 1 }}>
            {letter}. Registernachverfolgung
          </Typography>
          <Typography sx={{ ml: 2 }}>I. Eigentümer Nachverfolgung</Typography>
          <ProcessManagerLandRegisterTable
            processManagerArray={processManagerArray}
            setProcessManagerArray={setProcessManagerArray}
            relationsships={relationsships}
            setRelationships={setRelationships}
            legalClients={legalClients}
            legalPhases={legalPhases}
            legalReqStates={legalReqStates}
            enableShowType
          />
        </Box>
      ),
    });
  }

  if (pmsHandelsregister.length > 0) {
    sections.push({
      key: 'handelsregister',
      firstId: oldestId(pmsHandelsregister),
      render: letter => (
        <Box key='handelsregister' sx={{ mb: 2 }}>
          <Typography sx={{ mt: 1 }}>{letter}. Handelsregister</Typography>
          <ProcessManagerCommercialRegisterTable
            processManagerArray={processManagerArray}
            setProcessManagerArray={setProcessManagerArray}
            relationsships={relationsships}
            setRelationships={setRelationships}
            legalClients={legalClients}
            legalPhases={legalPhases}
            legalReqStates={legalReqStates}
            includeTypes={[20]}
          />
        </Box>
      ),
    });
  }

  if (pmsGesellschafterliste.length > 0) {
    sections.push({
      key: 'gesellschafterliste',
      firstId: oldestId(pmsGesellschafterliste),
      render: letter => (
        <Box key='gesellschafterliste' sx={{ mb: 2 }}>
          <Typography sx={{ mt: 1 }}>{letter}. Gesellschafterliste</Typography>
          <ProcessManagerCommercialRegisterTable
            processManagerArray={processManagerArray}
            setProcessManagerArray={setProcessManagerArray}
            relationsships={relationsships}
            setRelationships={setRelationships}
            legalClients={legalClients}
            legalPhases={legalPhases}
            legalReqStates={legalReqStates}
            includeTypes={[21]}
          />
        </Box>
      ),
    });
  }

  if (pmsHbci.length > 0) {
    sections.push({
      key: 'hbci',
      firstId: oldestId(pmsHbci),
      render: letter => (
        <Box key='hbci' sx={{ mt: 1, mb: 2 }}>
          <Stack
            direction='row'
            spacing={1}
            alignItems='baseline'
            sx={{ mb: 0.5 }}
          >
            <Typography>{letter}. Notaranderkonten</Typography>
          </Stack>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell width={50} />
                <TableCell>IBAN</TableCell>
                <TableCell>Bank</TableCell>
                <TableCell align='right'>Aktueller Saldo</TableCell>
                <TableCell align='right'>Zielsumme</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align='right' width={150}>
                  Aktionen
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pmsHbci.map(pm => {
                const idHbci = Number((pm.data as any)?.idHbciAccount);
                const account = hbciAccounts.find(
                  a => a.idHbciAccount === idHbci
                );
                return (
                  <ProcessManagerHbciRow
                    key={`pm-hbci-row-${pm.idProcessManager}`}
                    pm={pm}
                    account={account}
                    processManagerArray={processManagerArray}
                    setProcessManagerArray={
                      // Parent typisiert das Prop generisch als ``Function``;
                      // der HbciRow erwartet einen klassischen setState-Setter.
                      // Cast bewusst, weil der Parent das Pattern in der
                      // gesamten LegalTransaction-Komponente so reicht.
                      setProcessManagerArray as (
                        next:
                          | IProcessManager[]
                          | ((prev: IProcessManager[]) => IProcessManager[])
                      ) => void
                    }
                    legalClients={legalClients}
                    legalPhases={legalPhases}
                    legalReqStates={legalReqStates}
                    roles={roles}
                    idLegalTransaction={idLegalTransaction}
                  />
                );
              })}
            </TableBody>
          </Table>
        </Box>
      ),
    });
  }

  sections.sort((a, b) => a.firstId - b.firstId);

  const letters = ['a', 'b', 'c', 'd', 'e', 'f'];

  return (
    <>{sections.map((s, idx) => s.render(letters[idx] ?? `${idx + 1}`))}</>
  );
};
